import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { APP_ICONS } from 'src/app/icons/icon-provider';

/** Tab 2 for the candidate slider — file card + Download button.
 *  No inline PDF preview (mock only shows file card).
 *
 *  Presentational: the download itself lives in the parent slider
 *  (candidate-profile-slider `downloadPrimaryResume()`), which owns the
 *  shared `resumeDownloading` flag so this button and the header's
 *  "Download Resume" action disable + spin in lockstep. */
@Component({
  selector: 'app-cp-resume-card',
  standalone: true,
  imports: [CommonModule, NgIconComponent],
  providers: [provideIcons(APP_ICONS)],
  templateUrl: './cp-resume-card.component.html'
})
export class CpResumeCardComponent {
  /** `candidates/findCandidateById` response fragment — the component
   *  pulls `resumes[0]` out of this and reads filename + fileId + date. */
  @Input() candidate: any = null;

  /** Parent-owned in-flight flag (shared with the header action). */
  @Input() downloading = false;

  @Output() downloadClick = new EventEmitter<void>();

  get resume(): any | null {
    const list = this.candidate?.resumes;
    if (!Array.isArray(list) || list.length === 0) return null;
    return list[0];
  }
  get filename(): string {
    // Backend stores resumes on disk as `<uuid>_<originalFilename>.<ext>`,
    // and the API echoes that disk name back on `resume.fileName`. The
    // raw UUID prefix (e.g. `89614f2c-eda1-4283-8fd1-eee8e625d4a3_…`)
    // leaks into the slider as ugly UI noise — strip it when the leading
    // segment matches the canonical UUID v4 shape (8-4-4-4-12 hex). Real
    // filenames that happen to contain underscores
    // (e.g. `Joe_Smith_Resume.pdf`) are passed through untouched because
    // they don't match the UUID pattern.
    const raw = (this.resume?.fileName || this.resume?.name || '').toString().trim();
    if (!raw) return 'Resume';
    const uuidPrefix = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}_/;
    if (uuidPrefix.test(raw)) {
      const stripped = raw.replace(uuidPrefix, '').trim();
      return stripped || 'Resume';
    }
    return raw;
  }
  get uploadedOn(): any {
    return this.resume?.uploadedOn || this.candidate?.updatedOn || this.candidate?.createdOn;
  }
  get fileSize(): string | null {
    const bytes = this.resume?.size;
    if (typeof bytes !== 'number') return null;
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  get fileExtension(): string {
    const name = String(this.filename || '').toLowerCase();
    const idx = name.lastIndexOf('.');
    return idx >= 0 ? name.slice(idx + 1) : 'pdf';
  }
  get hasResume(): boolean { return !!this.resume; }

  download(): void {
    if (this.downloading || !this.hasResume) return;
    this.downloadClick.emit();
  }
}
